/**
 * The Board: carried-over days on top, then what needs a case, then the
 * day's suggestions; released entries on the right. Checking cards shows a
 * bar with Merge (same case only) and Release.
 */
import { CheckCircle2, GitMerge, Info, Lock, X } from "lucide-react";
import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ListCard } from "@/components/ui/list-card";

import { timeActions } from "../actions";
import { type BoardGroups, canMerge, carryLabel, releaseBlock } from "../model";
import type { TimeEntry } from "../types";
import { formatHours, totalHours } from "../utils";
import { BoardCard } from "./BoardCard";
import { ReleasedCard } from "./ReleasedCard";

type Props = {
  groups: BoardGroups;
  today: string;
  date: string;
  reviewTime: string;
  /** An entry to open on arrival (`?entry=`). */
  focusId?: string | null;
};

const SectionLabel: FC<{
  children: ReactNode;
  count?: number;
  aside?: ReactNode;
}> = ({ children, count, aside }) => (
  <div className="flex h-10 items-center gap-2 text-base font-medium text-fg">
    <span className="inline-flex items-center gap-1.5">{children}</span>
    <span className="flex-1" />
    {aside}
    {count !== undefined ? (
      <span className="text-sm font-normal text-fg-3 tabular-nums">
        {count}
      </span>
    ) : null}
  </div>
);

const reviewClock = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m || 0).padStart(2, "0")} ${suffix}`;
};

export const BoardView: FC<Props> = ({
  groups,
  today,
  date,
  reviewTime,
  focusId
}) => {
  const [openId, setOpenId] = useState<string | null>(focusId ?? null);
  const [checked, setChecked] = useState<string[]>([]);
  useEffect(() => {
    if (focusId) setOpenId(focusId);
  }, [focusId]);

  const all = useMemo(
    () => [
      ...groups.carry.flatMap((g) => g.entries),
      ...groups.needsCase,
      ...groups.toReview
    ],
    [groups]
  );
  useEffect(() => {
    setChecked((c) =>
      c.filter((id) => all.some((e) => e.id === id && e.individual_id))
    );
  }, [all]);

  const selected = all.filter((e) => checked.includes(e.id));
  const selectable = groups.toReview.filter((e) => e.individual_id);
  const allChecked =
    selectable.length > 0 && selectable.every((e) => checked.includes(e.id));
  const toggle = (id: string) =>
    setChecked((c) =>
      c.includes(id) ? c.filter((x) => x !== id) : [...c, id]
    );

  const card = (e: TimeEntry) => (
    <BoardCard
      key={e.id}
      entry={e}
      open={openId === e.id}
      checked={checked.includes(e.id)}
      onOpen={() => setOpenId(openId === e.id ? null : e.id)}
      onCheck={() => toggle(e.id)}
      onSplit={(ids) => setOpenId(ids[1] ?? ids[0] ?? null)}
    />
  );
  const stack = (list: TimeEntry[]) => (
    <div className="mb-4 flex flex-col gap-2.5">{list.map(card)}</div>
  );
  const isToday = date === today;

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-2">
      <div
        className="relative min-h-0 overflow-y-auto px-6 pt-2 pb-6"
        onClick={() => setOpenId(null)}
      >
        {checked.length ? (
          <div className="sticky top-0 z-10 mb-2 flex h-10 items-center gap-2.5 rounded-xl bg-foreground pr-1.5 pl-3.5 text-background">
            <span className="text-base font-medium tabular-nums">
              {checked.length} selected · {formatHours(totalHours(selected))}
            </span>
            <span className="flex-1" />
            <Button
              size="xs"
              variant="ghost"
              className="text-background hover:bg-background/10 hover:text-background"
              disabled={!canMerge(selected)}
              title={
                canMerge(selected)
                  ? undefined
                  : "Entries must be on the same case to merge"
              }
              onClick={async () => {
                const merged = await timeActions.merge(checked);
                if (merged) {
                  setChecked([]);
                  setOpenId(merged.id);
                }
              }}
            >
              <GitMerge /> Merge
            </Button>
            <Button
              size="xs"
              variant="secondary"
              onClick={async () => {
                if (await timeActions.release(selected)) setChecked([]);
              }}
            >
              Release
            </Button>
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label="Clear selection"
              className="text-background hover:bg-background/10 hover:text-background"
              onClick={() => setChecked([])}
            >
              <X />
            </Button>
          </div>
        ) : null}

        {groups.carry.map((g) => {
          const ready = g.entries.filter((e) => !releaseBlock(e));
          return (
            <div key={g.day.work_date} data-testid="carry-group">
              <SectionLabel
                count={g.entries.length}
                aside={
                  ready.length ? (
                    <Button
                      size="xs"
                      variant="secondary"
                      onClick={() => void timeActions.release(ready)}
                    >
                      Release All {ready.length}
                    </Button>
                  ) : null
                }
              >
                <span className="inline-flex items-center gap-1.5 text-warn">
                  <span className="size-1.5 rounded-full bg-warn" />
                  {carryLabel(g.day.work_date, today)} · Not Released Yet
                </span>
              </SectionLabel>
              {stack(g.entries)}
            </div>
          );
        })}

        {groups.needsCase.length ? (
          <>
            <SectionLabel count={groups.needsCase.length}>
              Needs a Case
            </SectionLabel>
            {stack(groups.needsCase)}
          </>
        ) : null}

        <SectionLabel
          count={groups.toReview.length}
          aside={
            selectable.length ? (
              <span className="inline-flex items-center gap-2 text-sm font-normal text-fg-3">
                <Checkbox
                  aria-label="Select all"
                  checked={
                    allChecked ? true : checked.length ? "indeterminate" : false
                  }
                  onCheckedChange={() =>
                    setChecked(allChecked ? [] : selectable.map((e) => e.id))
                  }
                />
                Select All
              </span>
            ) : null
          }
        >
          {isToday ? "To Review" : "Not Released"}
        </SectionLabel>
        {groups.toReview.length ? (
          stack(groups.toReview)
        ) : (
          <ListCard className="mb-3.5 flex-row items-center gap-2 px-3 py-2.5 text-base text-fg-2">
            <CheckCircle2 className="size-3.5 text-success" />
            {isToday
              ? `All caught up. New suggestions appear as you work, and again at ${reviewClock(
                  reviewTime
                )}.`
              : "Nothing left to release on this day."}
          </ListCard>
        )}
        {isToday ? (
          <div className="flex items-center gap-2 text-sm text-fg-3">
            <Info className="size-3.5" />
            <span>
              Time on the case you have open becomes a suggestion when you leave
              the case, or at {reviewClock(reviewTime)}.
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-col gap-2 overflow-y-auto border-t border-line-2 px-6 pt-2 pb-6 lg:border-t-0 lg:border-l">
        <SectionLabel
          aside={
            <span className="text-sm font-normal text-fg-3 tabular-nums">
              {groups.released.length === 1
                ? "1 entry"
                : `${groups.released.length} entries`}{" "}
              · {formatHours(totalHours(groups.released))}
            </span>
          }
        >
          Released
        </SectionLabel>
        {groups.released.length ? (
          <ListCard>
            {groups.released.map((e) => (
              <ReleasedCard key={e.id} entry={e} />
            ))}
          </ListCard>
        ) : (
          <ListCard className="px-3 py-2.5 text-base text-fg-3">
            Released entries land here and go to your firm's Billing Review.
            Only you see the left column.
          </ListCard>
        )}
        {groups.released.length ? (
          <span className="flex items-center gap-1.5 px-0.5 py-1 text-sm text-fg-3">
            <Lock className="size-3" />
            You can recall an entry until an admin approves it.
          </span>
        ) : null}
      </div>
    </div>
  );
};
